import type { Id } from '@odhaczka/core';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { DraftInput } from '../components/draft/DraftInput';
import { DraftPreview } from '../components/draft/DraftPreview';
import { useDraft } from '../components/draft/useDraft';
import { StorePicker } from '../components/StorePicker';
import { inputCls, primaryBtn } from '../components/ui/classes';
import { Field } from '../components/ui/Field';
import { PageHeader } from '../components/ui/PageHeader';
import { useNearbyStore } from '../data/geo';
import { createList, defaultListTitle, type NewListState } from '../data/lists';
import { isLidl, useStore, useStores } from '../data/stores';

/** Tekst przekazany w location.state: z SharePage albo z „Nowa lista na podstawie tej”. */
function fromState(state: unknown): Partial<NewListState> {
  if (!state || typeof state !== 'object') return {};
  const s = state as Record<string, unknown>;
  return {
    text: typeof s.text === 'string' ? s.text : '',
    storeId: typeof s.storeId === 'string' ? s.storeId : null,
    from: typeof s.from === 'string' ? s.from : null,
  };
}

export function NewListPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const stores = useStores();
  const [source] = useState(() => fromState(location.state));
  const [text, setText] = useState(source.text ?? '');
  const [title, setTitle] = useState('');
  const [titlePlaceholder] = useState(() => defaultListTitle());
  // undefined = użytkownik nie wybierał → sklep, w którym jestem (lokalizacja), albo ulubiony / ostatnio odwiedzany.
  const [storeId, setStoreId] = useState<Id | undefined>(undefined);
  const near = useNearbyStore();
  // Sklep listy, z której pochodzą pozycje (po scaleniach duplikatów; usunięty = brak).
  const sourceStore = useStore(source.storeId);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedId = storeId ?? sourceStore?.id ?? near.nearest?.storeId ?? stores?.[0]?.id ?? null;
  const store = stores?.find((s) => s.id === selectedId) ?? null;
  // Konkretne towary z paragonów Lidla: na listach do Lidla i bez sklepu.
  const draft = useDraft(text, false, { articles: !store || isLidl(store) });

  const submit = async (e: { preventDefault(): void }) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const { items, resolutions, pins } = await draft.finalize();
      if (items.length === 0) return;
      const id = await createList({ title, storeId: selectedId, items, resolutions, pins });
      navigate(`/lists/${id}`, { replace: true });
    } catch {
      setError('Nie udało się utworzyć listy. Spróbuj jeszcze raz.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <PageHeader title="Nowa lista" back="/" />
      <form onSubmit={submit} className="flex flex-1 flex-col">
        <main className="flex flex-1 flex-col gap-5 p-4">
          <DraftInput
            id="list-text"
            label={source.from ? `Pozycje z „${source.from}” — popraw przed zapisaniem` : 'Wklej listę'}
            value={text}
            onChange={setText}
            // Z udostępnionym tekstem nie otwieramy klawiatury — zasłoniłaby podgląd.
            autoFocus={!text}
          />

          <Field label="Nazwa (opcjonalnie)">
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={titlePlaceholder}
              enterKeyHint="done"
              // Enter w nazwie tylko chowa klawiaturę — listę tworzy się przyciskiem na dole.
              onKeyDown={(e) => {
                if (e.key !== 'Enter') return;
                e.preventDefault();
                e.currentTarget.blur();
              }}
              className={inputCls}
            />
          </Field>

          <DraftPreview draft={draft} />
        </main>

        <div className="safe-bottom sticky bottom-0 flex flex-col gap-2 border-t border-stone-200 bg-stone-50/95 px-4 pt-3 backdrop-blur dark:border-stone-800 dark:bg-stone-950/95">
          <button
            type="button"
            onClick={() => setPickerOpen(true)}
            className="flex min-h-12 items-center gap-3 rounded-xl border border-stone-300 bg-white px-4 text-left active:bg-stone-100 dark:border-stone-700 dark:bg-stone-900 dark:active:bg-stone-800"
          >
            <span className="text-xs font-semibold uppercase tracking-wide text-stone-500">Sklep</span>
            {store ? (
              <span className="min-w-0 flex-1 truncate font-medium">
                {store.name || 'Bez nazwy'}
                {store.chainName && <span className="font-normal text-stone-500"> · {store.chainName}</span>}
              </span>
            ) : (
              <span className="flex-1 font-medium text-brand-700 dark:text-brand-500">Wybierz sklep</span>
            )}
            {store && store.id === near.nearest?.storeId && <span className="shrink-0 text-sm text-brand-700 dark:text-brand-500">📍 jesteś tutaj</span>}
            <span aria-hidden className="text-xl text-stone-400">
              ›
            </span>
          </button>
          {error && <p className="text-center text-sm text-red-600">{error}</p>}
          <button disabled={busy || draft.items.length === 0} className={primaryBtn}>
            {busy ? 'Tworzę…' : `Utwórz listę (${draft.items.length})`}
          </button>
        </div>
      </form>

      <StorePicker open={pickerOpen} onClose={() => setPickerOpen(false)} onPick={setStoreId} selectedId={selectedId} />
    </>
  );
}
